import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, Outlet, useLocation } from 'react-router-dom';
import LeagueHeader from '../common/LeagueHeader';
import { League, V1Team, V1Standings, V1DraftAnalysis } from '../../types';
import { v1Api } from '../../services/api';
import Skeleton from '../common/Skeleton';
import Card from '../common/Card';
import Button from '../common/Button';
import GradingLoader from '../common/GradingLoader';

interface LeagueLayoutProps {
  children?: React.ReactNode;
}

export interface LeagueContextData {
  league: League | null;
  teams: V1Team[];
  standings: V1Standings | null;
  draftAnalysis: V1DraftAnalysis | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  espnLeagueId: string;
}

export const LeagueContext = React.createContext<LeagueContextData | null>(null);

const LeagueLayout: React.FC<LeagueLayoutProps> = ({ children }) => {
  const { espnLeagueId } = useParams<{ espnLeagueId: string }>();
  const location = useLocation();
  
  const [league, setLeague] = useState<League | null>(null);
  const [teams, setTeams] = useState<V1Team[]>([]);
  const [standings, setStandings] = useState<V1Standings | null>(null);
  const [draftAnalysis, setDraftAnalysis] = useState<V1DraftAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLeagueData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load league data first to get season information
      const leagueData = await v1Api.leagues.getByEspnId(espnLeagueId!);
      
      // Load remaining data with season information
      const [teamsData, standingsData] = await Promise.all([
        v1Api.leagues.getTeamsByEspnId(espnLeagueId!),
        v1Api.analytics.getStandings(parseInt(espnLeagueId!), leagueData.season)
      ]);
      
      setLeague(leagueData);
      setTeams(teamsData);
      setStandings(standingsData);
      setLoading(false);
      
      // Load draft analysis separately (it's only needed for draft tab)
      // Use ESPN league ID instead of internal database ID
      v1Api.analytics.getDraftAnalysis(parseInt(espnLeagueId!))
        .then(draftData => {
          setDraftAnalysis(draftData);
        })
        .catch(err => {
          console.warn('Draft analysis load failed:', err);
        });
      
    } catch (err) {
      setError('Failed to load league data');
      console.error('League fetch error:', err);
      setLoading(false);
    }
  }, [espnLeagueId]);

  useEffect(() => {
    if (espnLeagueId) {
      fetchLeagueData();
    }
  }, [espnLeagueId, fetchLeagueData]);

  // Prevent navigation back to home page
  useEffect(() => {
    // Push multiple identical states to prevent back navigation to home
    const currentUrl = window.location.href;
    
    // Replace current state and push additional states
    window.history.replaceState(null, '', currentUrl);
    window.history.pushState(null, '', currentUrl);
    window.history.pushState(null, '', currentUrl);
    
    // Add a popstate listener to handle back navigation attempts
    const handlePopState = (event: PopStateEvent) => {
      // Always stay on current league page by pushing the current state again
      window.history.pushState(null, '', currentUrl);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [espnLeagueId]);

  // handleTeamClick function moved to child components that need it

  // Determine active section from URL
  const activeSection = location.pathname.split('/').pop() || 'overview';

  if (!espnLeagueId) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Invalid league ID</h2>
          <Link to="/">
            <Button className="mt-4">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-primary">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-12 w-80 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <GradingLoader label="Grading the season…" size="lg" className="py-16" />
        </div>
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <Card className="p-6 text-center">
          <div className="text-accent-danger mb-4">{error || 'Failed to load league'}</div>
          <Button onClick={fetchLeagueData} className="mr-4">Retry</Button>
          <Link to="/">
            <Button variant="secondary">Back to Home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const contextValue: LeagueContextData = {
    league,
    teams,
    standings,
    draftAnalysis,
    loading,
    error,
    refetch: fetchLeagueData,
    espnLeagueId: espnLeagueId!
  };

  // Metadata line: "2025 · 12 teams · 14-week regular season · 16 rounds"
  const metaParts: string[] = [`${league.season}`, `${league.team_count} teams`];
  const regWeeks = league.regular_season_weeks ||
    (standings?.summary ? Math.floor(standings.summary.regular_season_games / (league.team_count / 2)) : null);
  if (regWeeks) metaParts.push(`${regWeeks}-week regular season`);
  const rounds = league.summary?.total_picks
    ? Math.floor(league.summary.total_picks / league.team_count)
    : (draftAnalysis?.summary.total_picks ? Math.floor(draftAnalysis.summary.total_picks / league.team_count) : null);
  if (rounds) metaParts.push(`${rounds} rounds`);
  const metaLine = metaParts.join('  ·  ');

  // Champion strip data
  const champion = standings?.summary?.champion;
  const secondPlace = standings?.standings?.find(t => t.rank === 2);
  const thirdPlace = standings?.standings?.find(t => t.rank === 3);
  const rsLeader = standings?.summary?.regular_season_leader;

  const recordStr = (wins?: number, losses?: number, pct?: number) =>
    `${wins ?? 0}-${losses ?? 0} · ${((pct ?? 0) * 100).toFixed(1)}%`;

  const StripCell: React.FC<{
    label: string; name?: string; sub?: string; tickColor: string; emphasis?: boolean;
  }> = ({ label, name, sub, tickColor, emphasis }) => (
    <div className="flex items-stretch gap-3">
      <div className="w-[3px] rounded-full flex-shrink-0" style={{ background: tickColor }} />
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-text-secondary">{label}</p>
        <p className={`font-semibold text-text-primary truncate ${emphasis ? 'text-xl' : 'text-base'}`}>{name || '—'}</p>
        {sub && <p className="font-mono text-sm text-text-secondary mt-0.5">{sub}</p>}
      </div>
    </div>
  );

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'draftboard', label: 'Draft' },
    { key: 'awards', label: 'Awards' },
  ];

  return (
    <LeagueContext.Provider value={contextValue}>
      <div className="min-h-[100dvh] bg-background-primary">
        <LeagueHeader
          showBackButton={true}
          leagueName={league.name || 'League'}
          espnLeagueId={espnLeagueId}
          season={league.season}
          availableSeasons={[league.season]}
          seasonSwitchEnabled={false}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* League title + metadata line */}
          <div className="mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold text-text-primary tracking-tight mb-1.5">
              {league.name || 'League'}
            </h1>
            <p className="font-mono text-sm text-text-secondary">{metaLine}</p>
          </div>

          {/* Champion strip — one panel, hairline dividers, champion larger */}
          {champion && (
            <Card className="p-5 mb-8">
              <div className="hidden sm:grid sm:grid-cols-[1.5fr_1fr_1fr_1.5fr] sm:divide-x sm:divide-fieldline">
                <div className="pr-5">
                  <StripCell label="Champion" name={champion.team_name}
                    sub={recordStr(champion.total_season.wins, champion.total_season.losses, champion.total_season.win_percentage)}
                    tickColor="#2FBF71" emphasis />
                </div>
                <div className="px-5">
                  <StripCell label="2nd Place" name={secondPlace?.team_name}
                    sub={secondPlace ? recordStr(secondPlace.total_season.wins, secondPlace.total_season.losses, secondPlace.total_season.win_percentage) : undefined}
                    tickColor="rgba(139,146,168,0.6)" />
                </div>
                <div className="px-5">
                  <StripCell label="3rd Place" name={thirdPlace?.team_name}
                    sub={thirdPlace ? recordStr(thirdPlace.total_season.wins, thirdPlace.total_season.losses, thirdPlace.total_season.win_percentage) : undefined}
                    tickColor="rgba(139,146,168,0.35)" />
                </div>
                <div className="pl-5">
                  <StripCell label="Reg. Season Leader" name={rsLeader?.team_name}
                    sub={rsLeader ? recordStr(rsLeader.regular_season?.wins, rsLeader.regular_season?.losses, rsLeader.regular_season?.win_percentage) : undefined}
                    tickColor="rgba(139,146,168,0.6)" />
                </div>
              </div>
              {/* Mobile: compact 2x2 grid in one panel */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-4 sm:hidden">
                <StripCell label="Champion" name={champion.team_name}
                  sub={recordStr(champion.total_season.wins, champion.total_season.losses, champion.total_season.win_percentage)}
                  tickColor="#2FBF71" />
                <StripCell label="2nd Place" name={secondPlace?.team_name}
                  sub={secondPlace ? recordStr(secondPlace.total_season.wins, secondPlace.total_season.losses, secondPlace.total_season.win_percentage) : undefined}
                  tickColor="rgba(139,146,168,0.6)" />
                <StripCell label="3rd Place" name={thirdPlace?.team_name}
                  sub={thirdPlace ? recordStr(thirdPlace.total_season.wins, thirdPlace.total_season.losses, thirdPlace.total_season.win_percentage) : undefined}
                  tickColor="rgba(139,146,168,0.35)" />
                <StripCell label="Reg. Leader" name={rsLeader?.team_name}
                  sub={rsLeader ? recordStr(rsLeader.regular_season?.wins, rsLeader.regular_season?.losses, rsLeader.regular_season?.win_percentage) : undefined}
                  tickColor="rgba(139,146,168,0.6)" />
              </div>
            </Card>
          )}

          {/* Tab Navigation — underline segmented control, sticky on scroll */}
          <div className="sticky top-16 z-30 -mx-4 px-4 sm:mx-0 sm:px-0 bg-background-primary/95 backdrop-blur-sm mb-8">
            <div className="flex gap-6 border-b border-fieldline">
              {tabs.map(({ key, label }) => (
                <Link
                  key={key}
                  to={`/leagues/espn/${espnLeagueId}/${key}`}
                  className={`relative py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none ${
                    activeSection === key ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {label}
                  {activeSection === key && (
                    <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-accent-primary rounded-full" />
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Outlet for nested routes */}
          <Outlet />
        </div>
      </div>
    </LeagueContext.Provider>
  );
};

export default LeagueLayout;